import { useMemo } from 'react';
import { Field, FieldArray, FormikConfig } from 'formik';
import { Checkbox, Typography, FormControlLabel } from '@mui/material';
import {
  Row,
  Col,
  useAlert,
  AddButton,
  Responsive,
  InputField,
  FlexLayout,
  ButtonTypes,
  AuthPOLayout,
} from '@gv/triage-components';

import { useAppSelector } from 'store';
import { selectAuthClient } from 'store/slices/auth';
import { useUpdateCurrentMutation } from 'store/api/clients';
import { RemoveFieldButton, PetOwnerFooterButtons } from 'components';

import { Config } from '../../config';

import * as Styles from './styles';
import { FormValues, ClientEmailsConfigProps } from './types';
import { getClientPayload, getInitialValues, validationSchema } from './config';

export const ClientEmailsConfig = ({
  onBack,
  onSend,
  isAppointmentCard,
}: ClientEmailsConfigProps) => {
  const client = useAppSelector(selectAuthClient);
  const isChatPage = location.pathname.includes('front-desk');
  // this is for push

  const { handleError } = useAlert();

  const [updateClient] = useUpdateCurrentMutation();

  const initialValues = useMemo(() => getInitialValues(client), [client]);

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = async (
    values,
    { setSubmitting }
  ) => {
    const payload = getClientPayload(values, client);
    if (!payload) {
      setSubmitting(false);
      return;
    }
    try {
      await updateClient(payload).unwrap();
      await onSend();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <AuthPOLayout<FormValues>
      validateOnBlur
      validateOnChange
      enableReinitialize
      onSubmit={onSubmit}
      title="Almost there"
      initialValues={initialValues}
      validationSchema={validationSchema}
      isAppointmentCard={isAppointmentCard}
      subtitle={
        isChatPage
          ? 'Please provide your email.'
          : 'Please provide your email, so we can send you a summary afterward.'
      }
      footer={({ values, isSubmitting }) => {
        // Disabled when submitting OR no emails OR consent not given
        let isSubmitDisabled = isSubmitting;
        if (!isSubmitDisabled) {
          const hasEmail = !!values.emails.filter(Boolean).length;
          isSubmitDisabled = !hasEmail || !values.consent;
        }
        return (
          <PetOwnerFooterButtons
            leftText="Back"
            onLeftClick={onBack}
            rightText="Send request"
            isRightDisabled={isSubmitDisabled}
            rightButtonType={ButtonTypes.Submit}
          />
        );
      }}
    >
      {({ values }) => (
        <Styles.Container>
          <FieldArray
            name="emails"
            render={({ push, remove }) => {
              const moreThenOneEmail = values.emails.length > 1;
              return (
                <Row>
                  <Col col={8}>
                    {values.emails.map((_: string, idx: number) => {
                      const isFirstField = idx === 0;

                      return (
                        <Row key={`email_${idx}`}>
                          <Col>
                            <FlexLayout gap={8}>
                              <InputField
                                name={`emails.${idx}`}
                                placeholder={`Email ${idx + 1}`}
                                label={idx === 0 ? 'Email' : undefined}
                              />

                              {moreThenOneEmail && (
                                <div>
                                  {isFirstField && (
                                    <Responsive.Desktop>
                                      <p>&nbsp;</p>
                                    </Responsive.Desktop>
                                  )}

                                  <RemoveFieldButton
                                    onClick={() => remove(idx)}
                                  />
                                </div>
                              )}
                            </FlexLayout>
                          </Col>
                        </Row>
                      );
                    })}

                    <AddButton
                      asRow
                      text="Add new email"
                      onClick={() => push('')}
                    />

                    {/* Privacy + consent block */}
                    <div style={{ marginTop: 24 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, fontSize: { sm: 18, xs: 16 } }}
                      >
                        Your Privacy Matters—Review & Accept
                      </Typography>

                      <Typography sx={{ mt: 1 }} variant="body1">
                        Please review our{' '}
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`${Config.mainWebsiteUrl}privacy-policy/`}
                          style={{
                            fontWeight: 700,
                            color: '#1976d2',
                            textDecoration: 'none',
                          }}
                        >
                          Privacy Policy
                        </a>{' '}
                        and{' '}
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`${Config.mainWebsiteUrl}terms-of-use/`}
                          style={{
                            fontWeight: 700,
                            color: '#1976d2',
                            textDecoration: 'none',
                          }}
                        >
                          T&amp;C
                        </a>{' '}
                        for the virtual visit on the links below.
                      </Typography>

                      {/* Formik Field bound checkbox */}
                      <div style={{ marginTop: 12 }}>
                        <Field name="consent">
                          {({ field }: any) => (
                            <FormControlLabel
                              label={
                                <Typography
                                  sx={{ fontWeight: 700, fontSize: '0.95rem' }}
                                >
                                  Give Consent
                                </Typography>
                              }
                              control={
                                <Checkbox
                                  {...field}
                                  checked={Boolean(field.value)}
                                  onChange={(e) => field.onChange(e)}
                                  inputProps={{ 'aria-label': 'Give consent' }}
                                />
                              }
                            />
                          )}
                        </Field>
                      </div>
                    </div>
                  </Col>
                </Row>
              );
            }}
          />
        </Styles.Container>
      )}
    </AuthPOLayout>
  );
};
